// i18n
import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useMemo } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Link from 'presentations/views/components/atoms/Link';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import Button from 'presentations/views/components/atoms/Button';
import ListLinkButton, {
  EventLink,
} from 'presentations/views/components/molecules/ListLinkButton';
import CopyMenuLink from 'presentations/views/components/molecules/CopyMenuLink';
import {
  eventTypePath,
  hostEventTypeEditPath,
  webPath,
} from 'presentations/routes/path';
import Avatar, { Group } from 'presentations/views/components/atoms/Avatar';
import {
  spacing,
  fontSize,
  fontWeight,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import { EventTypeListItem } from 'presentations/api/schema/event_type';
import {
  black,
  white,
  gray01,
  gray02,
  gray04,
  gray06,
  primary,
} from 'presentations/views/styles/color';
import UserEventTypeTagList from 'presentations/views/components/organisms/UserEventTypeTagList';
import { Role } from 'domains/models/event_type_user/role';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import { Status } from 'domains/models/event_type/status';
import { UserType } from 'domains/models/event_type_user/user_type';
import { transformImageUrl } from 'presentations/assets';
import { isEmail } from 'infras/validator';
import EventType from 'domains/models/event_type';
import { useRouter } from 'next/router';

export interface Props {
  teamSlug: string;
  eventType: EventTypeListItem;
  role: Role;
  onClickMenu?: any;
  onClickShareMenu?: (
    e: SyntheticEvent<HTMLAnchorElement>,
    eventType: EventTypeListItem,
  ) => void;
  isLoading?: boolean;
  type: 'event-type' | 'one-off';
  onClickActivation?: any;
  onCopy?: any;
  onClickCopyMenu?: any;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const eventType = props.eventType;
  const router = useRouter();

  const { owners, members } = useMemo(() => {
    const owners = [];
    const members = [];
    for (const user of eventType.users) {
      if (user.userType === UserType.Host) {
        owners.push(user);
      } else {
        members.push(user);
      }
    }
    return { owners, members };
  }, [eventType.users]);

  // 予約ページ自体が有効もしくはカスタム日程調整を表示しているなら有効表示にする
  const isEnabled =
    eventType.status === Status.Enable || props.type === 'one-off';

  const onClickShareMenu = (e: SyntheticEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!props.onClickShareMenu) return;
    props.onClickShareMenu(e, eventType);
  };

  const renderContent = useMemo(
    () => (
      <>
        <EventTitle>
          <EventCalendarColor color={eventType.color} />
          {isEnabled ? (
            <EventTitleLink href={hostEventTypeEditPath(eventType.uid)}>
              {eventType.name}
            </EventTitleLink>
          ) : (
            eventType.name
          )}
        </EventTitle>
        {eventType.internalNote && (
          <InternalNote>
            <InternalNoteText>{eventType.internalNote}</InternalNoteText>
          </InternalNote>
        )}
        <UrlContainer>
          <UrlIcon name="open-in-new" />
          <Url
            href={eventTypePath({
              teamSlug: props.teamSlug,
              eventTypeSlug: eventType.slug,
            })}
            target="_blank"
          >
            {eventTypePath({
              teamSlug: props.teamSlug,
              eventTypeSlug: eventType.slug,
            })}
          </Url>
        </UrlContainer>
        <UserList>
          {owners.length > 0 && (
            <Invitor>
              <Label>{translate('Owner')}</Label>
              <GroupWrap max={4}>
                {owners.map((user) => (
                  <User
                    key={user.uid}
                    src={transformImageUrl(user.profileImage, {
                      width: 28,
                      height: 28,
                      dpr: 2,
                    })}
                    alt={user.name || undefined}
                    className={'MuiAvatarGroup-avatar'}
                  >
                    {user.name.charAt(0)}
                  </User>
                ))}
              </GroupWrap>
            </Invitor>
          )}
          {members.length > 0 && (
            <Invitee>
              <Label>{translate('Member')}</Label>
              <GroupWrap max={4}>
                {members.map((user) => {
                  return isEmail(user.uid) ? (
                    <StyledAvatar
                      key={user.uid}
                      alt={user.name}
                      style={{ zIndex: 0 }}
                      color={EventType.generateEventTypeColorById(
                        user.name.length,
                      )}
                      className={'MuiAvatarGroup-avatar'}
                    >
                      {user.name.charAt(0)}
                    </StyledAvatar>
                  ) : (
                    <User
                      key={user.uid}
                      src={transformImageUrl(user.profileImage, {
                        width: 28,
                        height: 28,
                        dpr: 2,
                      })}
                      alt={user.name || user.uid}
                      style={{ zIndex: 0 }}
                      className={'MuiAvatarGroup-avatar'}
                    >
                      {user.name.charAt(0)}
                    </User>
                  );
                })}
              </GroupWrap>
            </Invitee>
          )}
        </UserList>

        <PageLinkContainer>
          <LinkIcon name="chevron-right" />
          <PageLink
            href={eventTypePath({
              teamSlug: props.teamSlug,
              eventTypeSlug: eventType.slug,
            })}
            target="_blank"
          >
            {t('Schedule')}
          </PageLink>
        </PageLinkContainer>
      </>
    ),
    [eventType, owners, members],
  );
  return (
    <Card className="tour-target-eventType-item" isEnabled={isEnabled}>
      <Content isEnabled={isEnabled}>
        <Header>
          {/* <CoverImageContainer>
            <CoverImage
              src={eventType.coverImage || 'dummy.png'}
              alt={eventType.name}
              width={260}
              height={260}
              fit="crop"
            />
          </CoverImageContainer> */}
          <Block>
            <TagList>
              <UserEventTypeTagList
                duration={eventType.duration}
                location={eventType.location}
              />
            </TagList>
            <ShownPCSite>{renderContent}</ShownPCSite>
          </Block>
        </Header>
        <ShownSPSite>{renderContent}</ShownSPSite>
      </Content>
      <ActionList>
        {isEnabled ? (
          <>
            <ActionListItem className="tour-target-eventType-item-copy-link">
              <CopyMenuLink
                isHideIcon
                onCopy={props.onCopy}
                // onClickMenu={(e: SyntheticEvent) =>
                //   props.onClickCopyMenu(e, eventType)
                // }
                copiedContent={webPath(
                  eventTypePath({
                    teamSlug: props.teamSlug,
                    eventTypeSlug: eventType.slug,
                  }),
                  router.locale,
                )}
              />
            </ActionListItem>
            <ActionListItem>
              <ListLinkButton
                label={t('Share')}
                onClick={onClickShareMenu}
                withMenu
              />
            </ActionListItem>
          </>
        ) : (
          <ActionListItem>
            <ActivationButton
              color="primary"
              size="large"
              onClick={props.onClickActivation}
            >
              {t('Activation')}
            </ActivationButton>
          </ActionListItem>
        )}
      </ActionList>
      <MenuContainer className="tour-target-eventType-item-setting">
        {props.role <= Role.Editor && (
          <IconButton
            onClick={(e: SyntheticEvent) => props.onClickMenu(e, eventType)}
          >
            <SettingIcon name="settings" />
          </IconButton>
        )}
      </MenuContainer>
    </Card>
  );
}

export function LoadingComponent() {
  return (
    <Card isEnabled>
      <Content isEnabled>
        <Header>
          <Block>
            <TagList>
              <Skeleton width="100px" variant="text" />
            </TagList>
            <EventTitle>
              <Skeleton width="100%" variant="text" />
            </EventTitle>
            <UrlContainer>
              <Skeleton width="100%" variant="text" />
            </UrlContainer>
            <Skeleton width="100%" variant="text" />
            <UserList>
              <Invitor>
                <Skeleton width={28} height={28} variant="circular" />
              </Invitor>
            </UserList>
          </Block>
        </Header>
        <ShownSPSite></ShownSPSite>
      </Content>
    </Card>
  );
}

const GroupWrap = styled(Group)`
  .MuiAvatar-root {
    width: 32px;
    height: 32px;
    font-size: ${fontSize(14)};
    font-weight: bold;
  }
`;
const Card = styled.div<{ isEnabled: boolean }>`
  display: flex;
  position: relative;
  background: ${({ isEnabled }) => (isEnabled ? white : gray06)};
  padding: ${spacing(32)};
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};
  ${mediaMax.desktop} {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: ${spacing(24)};
  }
`;
const Content = styled.div<{ isEnabled: boolean }>`
  width: 100%;
  ${({ isEnabled }) =>
    !isEnabled &&
    css`
      opacity: 0.6;
    `}
`;
const Header = styled.div`
  display: flex;
  width: 100%;
  color: ${gray01};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(8)};
  }
`;
const TagList = styled.div`
  margin-bottom: ${spacing(8)};
  display: flex;
  ${mediaMax.tablet} {
    margin-bottom: 0;
  }
`;
const Block = styled.div`
  width: 100%;
  ${mediaMax.tablet} {
    margin-right: ${spacing(24)};
  }
`;
const EventTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  margin-bottom: ${spacing(2)};
`;
const EventTitleLink = styled(Link)`
  color: ${black};
  flex: 1;
`;
const UserList = styled.div`
  display: flex;
  align-items: center;
`;
const Invitor = styled.div`
  margin-right: ${spacing(24)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    margin-right: ${spacing(16)};
  }
`;
const Invitee = styled.div`
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
  }
`;
const User = styled(Avatar)`
  width: 28px;
  height: 28px;
  font-weight: ${fontWeight('bold')};

  .MuiAvatarGroup-avatar:first-of-type {
    margin-left: 0;
  }
`;
const StyledAvatar = styled(Avatar)<{ color: string }>`
  width: 28px;
  height: 28px;
  font-size: ${fontSize(12)};
  font-weight: initial;

  ${({ color }) => css`
    background-color: ${color};
  `}
`;

const Label = styled.span`
  margin-right: ${spacing(6)};
  color: ${gray02};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(12)};
`;

const MenuContainer = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 ${spacing(32)};
  border-left: 1px solid ${gray04};

  ${mediaMax.desktop} {
    display: flex;
    flex-direction: row;
    justify-content: initial;
    margin-top: ${spacing(24)};
    padding: 0;
    border-left: 0;
  }
  ${mediaMax.tablet} {
    justify-content: space-between;
    margin-top: ${spacing(16)};
    padding: 0;
    border-left: 0;
  }
`;
const ActionListItem = styled.div`
  :not(:first-of-type) {
    margin-top: ${spacing(16)};
  }
  ${mediaMax.desktop} {
    :not(:first-of-type) {
      margin-top: 0;
      margin-left: ${spacing(20)};
    }
  }
  ${mediaMax.tablet} {
    flex: 1;
    :not(:first-of-type) {
      margin-left: ${spacing(8)};
    }
    &:last-of-type {
      ${mediaMax.tablet} {
        margin-bottom: 0;
      }
    }
    ${EventLink} {
      width: 100%;
    }
  }
`;
const ShownPCSite = styled.div`
  ${mediaMax.tablet} {
    display: none;
  }
`;
const ShownSPSite = styled.div`
  display: none;
  ${mediaMax.tablet} {
    display: block;
  }
`;
const ActivationButton = styled(Button)`
  width: 213px;
  height: 52px;
  ${mediaMax.tablet} {
    width: 100%;
  }
`;

const EventCalendarColor = styled.span<{ color: string }>`
  display: inline-block;
  margin-right: ${spacing(8)};
  width: 8px;
  height: 8px;
  border-radius: ${borderRadius(4)};
  background-color: ${({ color }) => color};
`;

const SettingIcon = styled(Icon)`
  color: ${gray01};
`;
const PageLinkContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing(8)};
`;
const InternalNote = styled.div`
  color: ${gray01};
  display: flex;
  align-items: center;
  padding-right: ${spacing(8)};
  margin-bottom: ${spacing(8)};
  ${mediaMax.tablet} {
    padding-right: 0;
  }
`;
const LinkIcon = styled(Icon)`
  color: ${primary};
  width: 18px !important;
  height: 18px !important;
  margin-right: ${spacing(4)};
`;
const PageLink = styled(Link)`
  color: ${black};
`;

const UrlContainer = styled.div`
  color: ${gray02};
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(8)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(4)};
  }
`;
const Url = styled(Link)`
  color: ${gray02};
  font-size: ${fontSize(12)};
`;
const UrlIcon = styled(Icon)`
  color: ${gray02};
  width: 14px !important;
  height: 14px !important;
  margin-right: ${spacing(4)};
`;

const PrivateIcon = styled(Icon)`
  color: ${gray01};
  width: 14px !important;
  height: 14px !important;
  margin-right: ${spacing(4)};
`;

const InternalNoteText = styled.div`
  flex: 1;
`;
